import React from 'react';
import { Button, Flex, Select, Stack, Switch, Text, TextInput, Textarea } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MeetingNoteTense, TemplateCreationData } from 'src/company/templates/types';
import { downloadTemplateJson } from 'src/company/templates/utils/templateExport';

export default function FinalizeTemplateStep(props: StepProps<TemplateCreationData>) {
  const { data, setData } = props;

  const handleTemplateNameChange = (value: string) => {
    setData({
      ...data,
      templateName: value,
    });
  };

  const handleTemplateDescriptionChange = (value: string) => {
    setData({
      ...data,
      templateDescription: value,
    });
  };

  const handleTenseChange = (value: string | null) => {
    setData({
      ...data,
      meetingNoteTense: (value as MeetingNoteTense) ?? 'THIRD_PERSON',
    });
  };

  const handleEligibleForSummarisationChange = (checked: boolean) => {
    setData(prev => ({
      ...prev,
      eligibleForSummarisation: checked,
      createdTemplate: prev.createdTemplate
        ? { ...prev.createdTemplate, eligibleForSummarisation: checked }
        : undefined,
    }));
  };

  const handleNextPress = async () => {
    const newData = { ...data };
    if (newData.createdTemplate) {
      newData.createdTemplate = {
        ...newData.createdTemplate,
        description: newData.templateDescription,
        displayName: newData.templateName,
      };
    }

    props.nextStep(newData);
  };

  const handleExport = () => {
    if (data.createdTemplate) {
      downloadTemplateJson(data.createdTemplate);
    }
  };

  const canGenerate = data.templateName.trim().length > 0;
  const isEditMode = !!data.createdTemplate;
  const isMeetingNote = data.templateType === 'MEETING_NOTE';

  const tenseOptions = [
    { value: 'THIRD_PERSON', label: 'Third person' },
    { value: 'FIRST_SECOND_PERSON', label: 'First & second person' },
  ];

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Template details"
      description="Enter a name and description for your template."
      continueText={'Continue'}
      canGoForward={canGenerate && !!data.templateName && !!data.templateDescription}
      hideProgress={true}
      nextStep={handleNextPress}
    >
      <Flex direction="column" gap="md">
        <SettingsList>
          <Flex direction="column" gap="xxxs" mb="sm">
            <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Template Configuration
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Set the name and description for your template
            </Text>
          </Flex>

          <Stack gap="sm">
            <TextInput
              label="Template name"
              placeholder="Enter template name"
              value={data.templateName}
              onChange={event => handleTemplateNameChange(event.currentTarget.value)}
              required
            />

            <Textarea
              label="Description"
              placeholder="Describe what this template is used for and when to use it"
              value={data.templateDescription || ''}
              onChange={event => handleTemplateDescriptionChange(event.currentTarget.value)}
              rows={3}
              required
            />

            {isMeetingNote && (
              <Select
                label="Writing style"
                description="Choose how pronouns are used in the meeting notes"
                data={tenseOptions}
                value={data.meetingNoteTense ?? 'THIRD_PERSON'}
                onChange={handleTenseChange}
              />
            )}

            {isMeetingNote && (
              <Switch
                label="Eligible for summarisation"
                description="When off, this template skips the summarisation step and ignores additional instructions"
                checked={data.eligibleForSummarisation ?? true}
                onChange={event =>
                  handleEligibleForSummarisationChange(event.currentTarget.checked)
                }
              />
            )}

            {isEditMode && (
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={handleExport}
                mt="xs"
              >
                Export to JSON
              </Button>
            )}
          </Stack>
        </SettingsList>
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

type SettingsListProps = {
  children: React.ReactNode;
};

function SettingsList({ children }: SettingsListProps) {
  return (
    <Flex direction="column" bg="gray.0" style={{ borderRadius: 12, overflowX: 'hidden' }}>
      <Flex
        direction="column"
        gap="xs"
        p="md"
        bd="1px solid var(--mantine-color-gray-1)"
        style={{ borderRadius: 12, overflowX: 'hidden' }}
        bg="white"
      >
        {children}
      </Flex>
    </Flex>
  );
}
